import type { NextPage } from 'next';
import styles from '../../styles/form/textInput.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, library } from '@fortawesome/fontawesome-svg-core';
import { joinClassnames } from 'helpers/classNames';
import { faLock, faEnvelope, faUser, faPlus, faGlobeAfrica } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { Country } from 'country-state-city';
import { useEffect, useMemo, useState } from 'react';
import { capitalise } from 'helpers/general';

// Add FontAwesome icons to the library
library.add(faLock, faEnvelope, faUser, faPlus, faGlobeAfrica);

interface CountryOption {
  value: string;
  label: string;
}

interface Props {
  text: string;
  placeholder?: string;
  onChange: (countryLabel: string) => void;
  icon?: IconName;
  type: string;
  color?: string;
  label?: string;
  smallPadding?: boolean;
  required?: boolean;
  disabled?: boolean;
  shadow?: boolean;
}

// Function to create a country object based on the country name
export const makeCountryObject = (countryName: string): CountryOption => {
  const country = Country.getAllCountries().find((c) => c.name === countryName);
  return {
    value: country ? country.isoCode : '',
    label: countryName || '',
  };
}

const CountryInputComponent: NextPage<Props> = ({
  text,
  placeholder,
  onChange,
  icon,
  type,
  color,
  label,
  smallPadding,
  required,
  disabled,
  shadow,
}) => {
  const options = useMemo(
    () => Country.getAllCountries().map(c => ({ label: c.name, value: c.isoCode })),
    []
  );

  const [value, setValue] = useState<CountryOption | null>(null);

  useEffect(() => {
    setValue(makeCountryObject(text));
  }, [text]);

  const textInputClassName = joinClassnames([
    styles.country_input,
    icon && styles.text_input_with_icon,
    smallPadding && styles.country_input_small_padding,
    color === 'white' && styles.make_input_white,
    shadow && styles.shadow,
  ]);

  const containerClassName = joinClassnames([
    styles.countryInput_container,
    color === 'white' && styles.make_input_white,
  ]);

  const handleChange = (selectedOption: CountryOption | null) => {
    setValue(selectedOption);
    onChange(selectedOption ? selectedOption.label : '');
  };

  return (
    <>
      {label && (
        <div className={styles.label}>
          {required ? '* ' : ''}{label}
        </div>
      )}

      <div className={containerClassName}>
        {icon && (
          <div className={styles.icon_wrapper}>
            <FontAwesomeIcon className={styles.icon} icon={['fas', icon]} />
          </div>
        )}

        <Select
          isDisabled={disabled}
          className={textInputClassName}
          placeholder={placeholder}
          options={options}
          value={value?.label !== "" ? value : null}
          onChange={handleChange}
        />
      </div>
    </>
  );
}

export default CountryInputComponent;
